import {
  createFormField,
  FieldDataType,
  FieldInputType,
  getEntityRefEntityTypeId,
  IFormField,
  IFormFieldMode,
  parseStringifyObjectValue,
} from '@migrate/utils/form/form'
import { action, makeObservable, observable, runInAction } from 'mobx'
import { DateRenderer } from '@migrate/utils/form/DateRenderer'
import { NumericRenderer } from '@migrate/utils/form/NumericRenderer'
import { PercentageRenderer } from '@migrate/utils/form/PercentageRenderer'
import { CurrencyRenderer } from '@migrate/utils/form/CurrencyRenderer'
import { SelectRenderer } from '@migrate/utils/form/SelectRenderer'
import { fromIsoString, fromLocalDate, fromNative } from '@migrate/utils/date'
import { fromNumberString } from '@migrate/utils/number'
import { getUsersStore, stores } from '@migrate/stores'
import { LocalesStore } from '@migrate/stores/LocalesStore'
import renderYesNo from '@migrate/utils/renderer/Yesno'
import { truncate } from '@migrate/utils/strings'
import { EntityTypeStore } from '@migrate/stores/EntityTypeStore'
import EntityItem from '@migrate/components/navigator/EntityItem'
import {
  Assumption,
  AssumptionValue,
  ConditionalAssumption,
  EntityAssumptionDetail,
  EntityStorageType,
} from '@portal/pkg-api/gen/navigator'
import { data } from '@portal/pkg-api/shared'
import { ConditionalAssumptionApi, EntityApi } from '@portal/pkg-api/navigator'
import { EntityBasic } from '@navigator/model'
import { EntityType } from '@migrate/models/EntityType'
import { LocalDate } from '@js-joda/core'
import FieldAssumptionProcessor from '@migrate/utils/form/FieldAssumptionProcessor'
import { faFile } from '@fortawesome/pro-solid-svg-icons'
import { DisplayableUser } from '@shared/model'
import i18n from 'i18next'

export class AssumptionStore {
  @observable public assumptionFields: IFormField[]
  @observable public assumptionFieldMap: any

  users: DisplayableUser[]
  conditionalSelects: ConditionalAssumption[]
  conditionalHiddens: ConditionalAssumption[]
  fieldAssumptionProcessor: FieldAssumptionProcessor

  protected entityType?: EntityType

  constructor() {
    this.assumptionFields = []
    this.assumptionFieldMap = {}
    this.users = []
    this.conditionalSelects = []
    this.conditionalHiddens = []
    this.fieldAssumptionProcessor = new FieldAssumptionProcessor([])

    this.users = getUsersStore().users
    makeObservable(this)
  }

  @action
  public onMessageReceived(data: any) {
    const f = this.findBulkInfoListField()
    if (f) {
      f.value = data
    }
  }

  protected setEntityType(entityType?: EntityType) {
    this.entityType = entityType
  }

  @action
  async loadConditionalAssumptions(entityTypeId: number) {
    const response = await data(ConditionalAssumptionApi.getConditionalAssumptions(entityTypeId))

    runInAction(() => {
      response.forEach((assumption) => {
        if (assumption.conditionalType === 'Select') {
          this.conditionalSelects.push(assumption)
        } else if (assumption.conditionalType === 'Hidden') {
          this.conditionalHiddens.push(assumption)
        }
      })
    })
  }

  @action
  setAssumptionFields(fields: IFormField[]) {
    this.assumptionFields = fields
    this.assumptionFields.forEach((field) => {
      this.assumptionFieldMap[field.id!] = field
    })
  }

  @action
  revertValue(field: IFormField, value: any) {
    const fieldToRevert = this.findFieldById(field!.id!)
    fieldToRevert.value = value
    field.state.editValue = undefined
  }

  findFieldById(id: number) {
    return this.assumptionFieldMap[id]
  }

  findBulkInfoListField() {
    return this.assumptionFields.find(
      (f) => f.meta.model.assumption.refName === 'M_bulkUploadInfoList',
    )
  }

  createFormField(
    assumption: Assumption,
    assumptionValue?: AssumptionValue | EntityAssumptionDetail,
  ) {
    const type = assumption.type

    const formField = createFormField(assumption.id.toString())
    formField.id = assumption.id
    formField.label = assumption.label
    formField.meta.model = assumptionValue ? assumptionValue : assumption
    formField.meta.required = assumption.required
    formField.meta.alphabetize = assumption.alphabetize
    formField.meta.variant = 'outlined'

    if (assumption.helpText) {
      formField.state.helperText = truncate(assumption.helpText, 100)
    }

    if (assumptionValue && assumptionValue.value && assumptionValue.value !== '') {
      formField.value = assumptionValue.value
    }

    if (formField.meta.model?.linkedDocumentId) {
      formField.meta.bottomAdornments = [
        { id: 'sourceFile', labelKey: 'navigator.detail.assumption.sourceFile', icon: faFile },
      ]
    }

    switch (type) {
      case 'textarea':
        return this.handleTextArea(assumption, assumptionValue!, formField)
      case 'date':
        return this.handleDateType(assumption, assumptionValue!, formField)
      case 'numeric':
      case 'currency':
      case 'percentage':
      case 'completion_percentage':
        return this.handleNumericType(assumption, assumptionValue!, formField)
      case 'select':
      case 'multiselect':
        return this.handleSelectType(assumption, assumptionValue!, formField)
      case 'yesno':
        return this.handleYesNoType(assumption, assumptionValue!, formField)
      case 'userlist':
      case 'orguserlist':
        return this.handleUserType(assumption, assumptionValue!, formField)
      case 'namedrange':
        return this.handleNamedRangeType(assumption, assumptionValue!, formField)
      case 'union':
        return this.handleUnionType(assumption, assumptionValue!, formField)
      case 'header':
        return this.handleHeaderType(assumption, assumptionValue!, formField)
      case 'image':
        return this.handleImageType(assumption, assumptionValue!, formField)
      case 'attachment':
        return this.handleAttachmentType(assumption, assumptionValue!, formField)
      case 'richtext':
        return this.handleRichTextType(assumption, assumptionValue!, formField)
      case 'parententitytype':
        return this.handleParentEntityType(assumption, assumptionValue!, formField)
      case 'entityref':
        return this.handleEntityRef(assumption, assumptionValue!, formField)
      case 'infolist':
        return this.handleInfoList(assumption, assumptionValue!, formField)
      case 'multiuser':
        return this.handleMultiUser(assumption, assumptionValue!, formField)
      case 'text':
        if (this.entityType?.storageType === EntityStorageType.TABULAR) {
          formField.meta.maxLength = assumption.alternateKey ? 255 : 4000
        }
        return formField
      case 'url':
        return this.handleUrl(assumption, assumptionValue!, formField)
      default:
        return formField
    }
  }

  handleTextArea(assumption: Assumption, assumptionValue: AssumptionValue, formField: IFormField) {
    formField.meta.control = FieldInputType.TextArea
    return formField
  }

  handleDateType(assumption: Assumption, assumptionValue: AssumptionValue, formField: IFormField) {
    formField.meta.type = FieldDataType.Date
    formField.meta.control = FieldInputType.Date
    formField.meta.dateFormat = 'MM/dd/yyyy'
    formField.meta.renderer = new DateRenderer()

    if (this.entityType?.storageType === EntityStorageType.TABULAR) {
      formField.meta.minDate = LocalDate.of(1901, 1, 1)
      formField.meta.maxDate = LocalDate.of(9999, 12, 31)
    }

    assumptionValue && assumptionValue.value && assumptionValue.value !== ''
      ? (formField.value = fromIsoString(assumptionValue.value!).toLocalDate())
      : (formField.value = null)

    return formField
  }

  handleNumericType(
    assumption: Assumption,
    assumptionValue: AssumptionValue,
    formField: IFormField,
  ) {
    formField.meta.renderer = new NumericRenderer()
    formField.meta.type = FieldDataType.Numeric

    this.assignNumericMetaData(assumption, assumptionValue, formField)

    const type = assumption.type

    // `m`/`b` shorthand only applies to numeric and currency (not percentage).
    formField.meta.supportsMagnitudeSuffix = type === 'numeric' || type === 'currency'

    if (type === 'percentage' || type === 'completion_percentage') {
      formField.meta.renderer = new PercentageRenderer()
    }

    if (type === 'currency') {
      if (assumption.selectOptions && assumption.selectOptions.length > 0) {
        formField.meta.control = FieldInputType.TextWithSelect
        formField.meta.options = assumption.selectOptions.map((option) => ({
          value: option.trim(),
          label: option.trim(),
          hidden: false,
        }))
        formField.meta.mode = IFormFieldMode.Edit
      }
      formField.meta.renderer = new CurrencyRenderer()
      formField.meta.value2 =
        assumptionValue && assumptionValue.value2
          ? assumptionValue.value2
          : assumption.selectOptions
            ? assumption.selectOptions[0]
            : ''

      if (!formField.meta.value2 && assumption && assumption.unit) {
        formField.meta.value2 = assumption.unit
        formField.meta.unit = ''
        formField.meta.model.unit = ''
      }

      // todo: ILM has all currency units default to $ for non-defined assumptions
      //  we should likley set this per org or something and change it to a function to determine
      if (!formField.meta.value2 && !formField.meta.unit) {
        formField.meta.unit = '$'
      }
    }

    if (assumptionValue && assumptionValue.value) {
      formField.value =
        typeof formField.value === 'string'
          ? fromNumberString(assumptionValue.value).toNumber()
          : formField.value
    }
    return formField
  }

  assignNumericMetaData(
    assumption: Assumption,
    assumptionValue: AssumptionValue,
    formField: IFormField,
  ) {
    formField.meta.min = assumption.min
    formField.meta.max = assumption.max

    if (assumption.unit) {
      formField.meta.unit = assumption.unit
    }

    if (assumption.format) {
      formField.meta.format = assumption.format
    }

    if (assumption.fractionDigits !== undefined) {
      formField.meta.fractionDigits = assumption.fractionDigits
    }
  }

  handleYesNoType(assumption: Assumption, assumptionValue: AssumptionValue, formField: IFormField) {
    formField.meta.control = FieldInputType.Select

    formField.meta.selectMeta = {
      clearable: true,
    }

    formField.meta.options = [
      {
        value: 'Yes',
        label: renderYesNo('true'),
        hidden: false,
      },
      {
        value: 'No',
        label: renderYesNo('false'),
        hidden: false,
      },
    ]
    return formField
  }

  handleMultiUser(assumption: Assumption, assumptionValue: AssumptionValue, formField: IFormField) {
    formField.meta.control = FieldInputType.MultiUser

    //ILM-5350 - fields should respect when not editable at the entityAssumption definition
    formField.meta.disabled =
      assumptionValue && assumptionValue.editable !== undefined ? !assumptionValue.editable : false

    return formField
  }

  handleSelectType(
    assumption: Assumption,
    assumptionValue: AssumptionValue,
    formField: IFormField,
  ) {
    const type = assumption.type

    if (type === 'multiselect') {
      formField.meta.type = FieldDataType.Array
      formField.meta.control = FieldInputType.MultiSelect
    } else {
      formField.meta.control = FieldInputType.Select
    }

    const options = this.getSelectOptions(assumption)

    formField.meta.selectMeta = {
      clearable: true,
    }

    formField.meta.options = options!.map((value) => {
      return {
        value: value.trim(),
        label: value.trim(),
        hidden: false,
      }
    })
    return formField
  }

  getSelectOptions(assumption: Assumption) {
    const options = assumption.selectOptions || []
    if (assumption.alphabetize) {
      options.sort()
    }
    return options
  }

  handleUserType(assumption: Assumption, assumptionValue: AssumptionValue, formField: IFormField) {
    formField.meta.control = FieldInputType.Select
    formField.meta.renderer = new SelectRenderer()
    formField.meta.selectMeta = {
      clearable: true,
    }

    formField.meta.options = this.users.map((user) => {
      return {
        value: user.identifier,
        label: user.displayName,
        hidden: false,
        disable: !user.active || !user.enabled,
        group: user.enabled ? 'none' : 'inactive',
        model: user,
      }
    })

    formField.meta.options = formField.meta.options
      .sort((a, b) => a.label.localeCompare(b.label))
      .sort((a, b) => (b.group === 'none' ? 1 : 0) - (a.group === 'none' ? 1 : 0))

    if (assumptionValue) {
      let v: any = assumptionValue.value

      this.users.forEach((user) => {
        if (v === user.givenName + ' ' + user.familyName) {
          v = user.identifier
        }
      })

      formField.value = v ? v : ''
    }
    return formField
  }

  handleNamedRangeType(
    assumption: Assumption,
    assumptionValue: AssumptionValue,
    formField: IFormField,
  ) {
    formField.meta.control = FieldInputType.NamedRange

    return formField
  }

  handleUnionType(assumption: Assumption, assumptionValue: AssumptionValue, formField: IFormField) {
    formField.meta.control = FieldInputType.Union

    return formField
  }

  handleHeaderType(
    assumption: Assumption,
    assumptionValue: AssumptionValue,
    formField: IFormField,
  ) {
    formField.meta.control = FieldInputType.Header
    formField.meta.type = FieldDataType.String
    // Headers must not be blank for NR's and Unions to work correctly.
    formField.meta.required = true
    formField.value = parseStringifyObjectValue(assumptionValue?.value)
    return formField
  }

  handleImageType(assumption: Assumption, assumptionValue: AssumptionValue, formField: IFormField) {
    formField.meta.control = FieldInputType.Image
    return formField
  }

  handleAttachmentType(
    assumption: Assumption,
    assumptionValue: AssumptionValue,
    formField: IFormField,
  ) {
    formField.meta.control = FieldInputType.Attachment
    formField.meta.additionalProps = assumption.attachmentMeta
    return formField
  }

  handleInfoList(assumption: Assumption, assumptionValue: AssumptionValue, formField: IFormField) {
    formField.meta.control = FieldInputType.InfoList
    if (assumption.refName === 'M_bulkUploadInfoList') {
      if (!formField.value) {
        this.pollBulk()
      } else {
        formField.value = JSON.parse(formField.value)
      }
    }
    return formField
  }

  pollBulk() {}

  handleRichTextType(
    assumption: Assumption,
    assumptionValue: AssumptionValue,
    formField: IFormField,
  ) {
    formField.meta.control = FieldInputType.RichText
    return formField
  }

  handleParentEntityType(
    assumption: Assumption,
    assumptionValue: AssumptionValue,
    formField: IFormField,
  ) {
    formField.meta.type = FieldDataType.Array
    formField.meta.control = FieldInputType.ParentEntityType
    return formField
  }

  handleEntityRef(assumption: Assumption, assumptionValue: AssumptionValue, formField: IFormField) {
    formField.meta.type = FieldDataType.String
    formField.meta.control = FieldInputType.EntityRef
    formField.meta.entityTypeId = assumption?.entityRefEntityTypeId
    if (formField.meta.entityTypeId) {
      const entityType = stores[EntityTypeStore.STORE_NAME].findById(formField.meta.entityTypeId)
      formField.state.helperText = i18n.t('searchFor', {
        item: entityType?.name ?? i18n.t('common.entity'),
      })
    }

    formField.meta.optionProvider = async (field, search) => {
      const entityType = getEntityRefEntityTypeId(field)
      const response = await data(
        EntityApi.getEntityBasicsPaged(
          undefined,
          undefined,
          undefined,
          'shortName',
          search,
          entityType ? [entityType] : undefined,
          'entityNameAndIdOnly',
          0,
        ),
      )
      return response.content!.map((value) => {
        return { value: value.id, label: value.shortName, hidden: false, model: value }
      })
    }
    formField.meta.renderOptionProvider = (props, option) => {
      const entity: EntityBasic = option.model
      const type = stores[EntityTypeStore.STORE_NAME].findById(entity.entityTypeId)
      if (!type) {
        return <li {...props}>{entity.shortName}</li>
      }
      return (
        <li {...props}>
          <EntityItem entityType={type} id={entity.id} name={entity.shortName} />
        </li>
      )
    }
    formField.meta.labelProvider = async (value) => {
      if (value) {
        try {
          const response = await data(
            EntityApi.getEntity(value, { ignore404: true, mercatusNotifyOnError: false }),
          )
          formField.meta.model.label = response.shortName
          return response.shortName
        } catch (e) {
          console.log(e)
          return value
        }
      }
      return ''
    }

    return formField
  }

  handleUrl(assumption: Assumption, assumptionValue: AssumptionValue, formField: IFormField) {
    formField.meta.type = FieldDataType.String
    formField.meta.control = FieldInputType.Url
    formField.meta.entityTypeId = assumption?.entityRefEntityTypeId

    return formField
  }

  processConditionalSelects() {
    this.conditionalSelects.forEach((conditional) => {
      const field = this.findFieldById(conditional.parentAssumptionId)
      if (field) {
        const rawMap = JSON.parse(conditional.valueMap) as Record<string, string>
        const valueMap: any = {}
        for (const id in rawMap) {
          if (Object.hasOwn(rawMap, id)) {
            const childValues: string[] = rawMap[id].split(',')
            valueMap[id] = childValues || []
          }
        }

        if (!field.meta.conditionalSelects) {
          field.meta.conditionalSelects = []
        }

        field.meta.conditionalSelects.push({
          childField: conditional.childAssumptionId.toString(),
          valueMap,
        })
      }
    })
  }

  processConditionalHiddens() {
    this.conditionalHiddens.forEach((conditional) => {
      const field = this.findFieldById(conditional.parentAssumptionId)
      if (field) {
        field.meta.conditionalHiddens = []
        const rawMap = JSON.parse(conditional.valueMap) as Record<string, string>
        for (const id in rawMap) {
          if (Object.hasOwn(rawMap, id)) {
            const childValues: string[] = rawMap[id].split(',')
            field.meta.conditionalHiddens.push({
              value: id,
              hiddenNames: childValues,
            })
          }
        }
      }
    })
  }

  sharedRowHeaderAssumptionFormatter(params: any, assumption: any) {
    const { assumption: assumptionDef } = assumption
    const { locale } = stores[LocalesStore.STORE_NAME]

    if (assumptionDef) {
      // override the type to the entityAssumptionDetail type
      assumptionDef.type = assumptionDef.namedRangeDataType! || 'string'

      assumption.value = params.value ? params.value.toString() : ''

      // just return string, no need run through renderer
      if (assumptionDef.type === 'string' && assumption.value) {
        return params.value
      }

      // capture dates here as well return as string
      if (assumptionDef.type === 'date' && assumption.value) {
        return fromLocalDate(
          fromNative(new Date(assumption.value)).toLocalDate(),
        ).toLocalizedString(locale)
      }

      // all other renderers
      const field = this.createFormField(assumptionDef, assumption)
      try {
        return field.meta.renderer ? field.meta.renderer.render(field) : field.value
      } catch {
        return params.value
      }
    }
    return params.value
  }

  findUserByProperty(property: 'identifier' | 'email' | 'fullname', value: string | number) {
    const users = this.users
    return property === 'fullname'
      ? users.find((user) => `${user.givenName} ${user.familyName}` === value)
      : users.find((user) => user[property] === value)
  }
}
